import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import Seo from "../../components/Seo/Seo";
import "./Home.css";
import { Link } from "react-router-dom";

export default function Home() {
  const { t } = useTranslation("common");
  const [textIndex, setTextIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [direction, setDirection] = useState('right');
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [fullscreenVideo, setFullscreenVideo] = useState(null);

  const videoRefs = useRef([]);
  const introAudioRef = useRef(null);

  // Rotating taglines for dynamic effect
  const taglines = [
    t("home.taglines.0"),
    t("home.taglines.1"),
    t("home.taglines.2")
  ];

  // Latest News
  const latestNews = [
    {
      id: 1,
      title: t("home.news.items.1.title"),
      excerpt: t("home.news.items.1.excerpt"),
      image: "https://res.cloudinary.com/dxgqcmf7j/image/upload/v1766394179/bt8ryuq98lftzr9zudpd.jpg",
      date: t("home.news.items.1.date")
    }
  ];

  // Investment Opportunities
  const investments = [
    {
      id: 1,
      title: t("home.investments.items.1.title"),
      description: t("home.investments.items.1.description"),
      mediaType: "video",
      media: "/media/investments/1.mp4",
      thumbnail: "/images/investments/invest-1.png"
    }
  ];

  // Royal Quotes
  const royalQuotes = [
    {
      id: 1,
      name: t("home.quotes.items.1.name"),
      title: t("home.quotes.items.1.title"),
      quote: t("home.quotes.items.1.quote"),
      image: "https://res.cloudinary.com/dxgqcmf7j/image/upload/v1766392282/zykd7vsdm7hccwpvqgmr.svg"
    },
    {
      id: 2,
      name: t("home.quotes.items.2.name"),
      title: t("home.quotes.items.2.title"),
      quote: t("home.quotes.items.2.quote"),
      image: "https://res.cloudinary.com/dxgqcmf7j/image/upload/v1766392147/rdz8hx8khgjsai3okj3y.svg"
    },
    {
      id: 3,
      name: t("home.quotes.items.3.name"),
      title: t("home.quotes.items.3.title"),
      quote: t("home.quotes.items.3.quote"),
      image: "https://res.cloudinary.com/dxgqcmf7j/image/upload/v1766392214/aqxfjlv29f2pza4swxem.svg"
    }
  ];

useEffect(() => {
  // لا يعاد التشغيل إذا سبق تشغيله في نفس الجلسة
  if (sessionStorage.getItem("heroIntroPlayed")) return;

  const audio = new Audio("/media/intro.m4a");
  audio.volume = 0; // نبدأ بصوت صفر
  introAudioRef.current = audio;

  let fadeInterval = null;

  const fadeIn = () => {
    fadeInterval = setInterval(() => {
      if (audio.volume < 0.6) {
        audio.volume = Math.min(audio.volume + 0.05, 0.6);
      } else {
        clearInterval(fadeInterval);
      }
    }, 100);
  };

  const tryPlay = () => {
    audio.play()
      .then(() => {
        sessionStorage.setItem("heroIntroPlayed", "true");
        fadeIn();
        cleanup();
      })
      .catch(() => {
        // المتصفح منع التشغيل — ننتظر أول تفاعل
      });
  };

  const handleFirstInteraction = () => {
    tryPlay();
  };

  const cleanup = () => {
    window.removeEventListener("click", handleFirstInteraction);
    window.removeEventListener("scroll", handleFirstInteraction);
    window.removeEventListener("touchstart", handleFirstInteraction);
  };

  // محاولة تشغيل تلقائي
  tryPlay();

  // fallback: أول تفاعل
  window.addEventListener("click", handleFirstInteraction, { once: true });
  window.addEventListener("scroll", handleFirstInteraction, { once: true });
  window.addEventListener("touchstart", handleFirstInteraction, { once: true });

  return () => {
    cleanup();
    if (fadeInterval) clearInterval(fadeInterval);
  };
}, []);


  // Hero tagline rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setDirection('right');
      setIsVisible(false);
      setTimeout(() => {
        setTextIndex((prev) => (prev + 1) % taglines.length);
        setIsVisible(true);
      }, 500);
    }, 4000);

    return () => clearInterval(interval);
  }, [taglines.length]);
  useEffect(() => {
    const exitHandler = () => {
      videoRefs.current.forEach((video) => {
        if (video) {
          video.classList.remove("video--fullscreen");
        }
      });
    };

    document.addEventListener("fullscreenchange", exitHandler);
    document.addEventListener("webkitfullscreenchange", exitHandler);

    return () => {
      document.removeEventListener("fullscreenchange", exitHandler);
      document.removeEventListener("webkitfullscreenchange", exitHandler);
    };
  }, []);

  // Royal quotes rotation
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setQuoteIndex((prev) => (prev + 1) % royalQuotes.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [isPaused, royalQuotes.length]);

  const handleDotClick = (index) => {
    if (index !== textIndex) {
      setDirection(index > textIndex ? 'right' : 'left');
      setIsVisible(false);
      setTimeout(() => {
        setTextIndex(index);
        setIsVisible(true);
      }, 500);
    }
  };

  const handleVideoHover = (index, isHovering) => {
    const video = videoRefs.current[index];
    if (!video) return;

    if (isHovering) {
      video.currentTime = 0;
      video
        .play()
        .catch(() => { });
    } else {
      video.pause();
      video.currentTime = 0;
    }
  };



  const handleQuoteNavigation = (newIndex) => {
    setQuoteIndex(newIndex);
  };

  return (
    <>
      <Seo title={t("seo.home.title")} description={t("seo.home.desc")} path="/" />

      {/* ENHANCED HERO */}
      <section className="homeHero" data-hero>
        <video className="homeHero__video" autoPlay muted loop playsInline>
          <source src="/media/landing.mp4" type="video/mp4" />
        </video>
        <div className="homeHero__overlay" />

        <div className="homeHero__particles">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${15 + Math.random() * 10}s`
              }}
            />
          ))}
        </div>

        <div className="homeHero__center">
          <div className="homeHero__logoWrapper">
            <img
              className="homeHero__logo"
              src="/brand/logo.png"
              alt="Terhal Logo"
            />
            <div className="homeHero__logoGlow" />
          </div>

          <div className="homeHero__tagWrapper">
            <h1
              className={`homeHero__tag ${isVisible ? 'visible' : 'hidden'} ${direction}`}
              key={textIndex}
            >
              {taglines[textIndex]}
            </h1>

            <div className="homeHero__dots">
              {taglines.map((_, i) => (
                <span
                  key={i}
                  className={`dot ${i === textIndex ? 'active' : ''}`}
                  onClick={() => handleDotClick(i)}
                />
              ))}
            </div>
          </div>
        </div>

        <div
          className="homeHero__scrollHint"
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
        >
          <div className="scrollHint__animation">
            <span className="scrollHint__dot"></span>
          </div>
          <p>{t("ui.scroll")}</p>
        </div>
      </section>

      {/* LATEST NEWS SECTION */}
      <section className="section newsSection">
        <div className="container">
          <div className="sectionHeader">
            <h2 className="sectionTitle">{t("home.news.sectionTitle")}</h2>
            <p className="sectionSubtitle">{t("home.news.sectionSubtitle")}</p>
          </div>

          <div className="newsGrid">
            {latestNews.map((news) => (
              <div key={news.id} className="newsCard">
                <div className="newsCard__image">
                  <img src={news.image} alt={news.title} />
                  <div className="newsCard__date">{news.date}</div>
                </div>
                <div className="newsCard__content">
                  <h3 className="newsCard__title">{news.title}</h3>
                  <p className="newsCard__excerpt">{news.excerpt}</p>
                  <Link to="/media-center" className="newsCard__link">
                    {t("home.news.items.1.readMore")}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTMENT OPPORTUNITIES SECTION */}
      <section className="section investmentSection">
        <div className="container">
          <div className="sectionHeader">
            <h2 className="sectionTitle">{t("home.investments.sectionTitle")}</h2>
            <p className="sectionSubtitle">{t("home.investments.sectionSubtitle")}</p>
          </div>

          <div className="investmentGrid">
            {investments.map((investment, index) => (
              <div key={investment.id} className="investmentCard">
                <div
                  className="investmentCard__media"
                  onMouseEnter={() => investment.mediaType === 'video' && handleVideoHover(index, true)}
                  onMouseLeave={() => investment.mediaType === 'video' && handleVideoHover(index, false)}
                >
                  {investment.mediaType === 'video' ? (
                    <>
                      <video
                        ref={(el) => (videoRefs.current[index] = el)}
                        poster={investment.thumbnail}
                        loop
                        playsInline
                        preload="metadata"
                        onLoadedData={(e) => {
                          e.target.currentTime = 0.01;
                        }}
                      >
                        <source src={investment.media} type="video/mp4" />
                      </video>
                      <div className="investmentCard__playIcon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                      <button
                        className="investmentCard__fullscreen"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFullscreenVideo(investments[index]);
                        }}
                      >
                        ⛶
                      </button>

                    </>
                  ) : (
                    <img src={investment.media} alt={investment.title} />
                  )}
                </div>
                <div className="investmentCard__content">
                  <h3 className="investmentCard__title">{investment.title}</h3>
                  <p className="investmentCard__description">{investment.description}</p>
                  <Link to="/investments" className="investmentCard__link">
                    {t("home.investments.items.1.discoverMore")}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>

            ))}
            {fullscreenVideo && (
              <div className="videoOverlay">
                <button
                  className="videoOverlay__close"
                  onClick={() => setFullscreenVideo(null)}
                >
                  ✕
                </button>

                <video
                  src={fullscreenVideo.media}
                  autoPlay
                  controls
                  playsInline
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ROYAL QUOTES SECTION */}
      <section className="section quotesSection">
        <div className="container">
          <div className="sectionHeader">
            <h2 className="sectionTitle">{t("home.quotes.sectionTitle")}</h2>
            <p className="sectionSubtitle">{t("home.quotes.sectionSubtitle")}</p>
          </div>

          <div
            className="quotesCarousel"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <button
              className="carouselArrow carouselArrow--prev"
              onClick={() => handleQuoteNavigation((quoteIndex - 1 + royalQuotes.length) % royalQuotes.length)}
              aria-label={t("home.quotes.navigation.previous")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            <div className="quotesWrapper">
              {royalQuotes.map((quote, index) => (
                <div
                  key={quote.id}
                  className={`quoteCard ${index === quoteIndex ? 'active' : ''} ${index < quoteIndex ? 'prev' : index > quoteIndex ? 'next' : ''
                    }`}
                >
                  <div className="quoteCard__content">
                    <div className="quoteCard__icon">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
                      </svg>
                    </div>
                    <p className="quoteCard__text">{quote.quote}</p>
                    <div className="quoteCard__author">
                      <h4>{quote.name}</h4>
                      <p>{quote.title}</p>
                    </div>
                  </div>
                  <div className="quoteCard__imageWrapper">
                    <img src={quote.image} alt={quote.name} />
                  </div>
                </div>
              ))}
            </div>

            <button
              className="carouselArrow carouselArrow--next"
              onClick={() => handleQuoteNavigation((quoteIndex + 1) % royalQuotes.length)}
              aria-label={t("home.quotes.navigation.next")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>

            <div className="carouselIndicators">
              {royalQuotes.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === quoteIndex ? 'active' : ''}`}
                  onClick={() => handleQuoteNavigation(index)}
                  aria-label={t("home.quotes.navigation.goTo", { index: index + 1 })}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}